// src/components/pages/ThanksWrapperArea.jsx
import React, { useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SessionContext } from "../../common/SessionContext";

const scrollTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });


const trackMetaPurchase = (parameters, eventID) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    return false;
  }

  try {
    window.fbq("track", "Purchase", parameters, { eventID });
    return true;
  } catch (error) {
    // Meta tracking must never interfere with the payment confirmation page.
    console.warn("Meta Pixel Purchase event was not sent:", error);
    return false;
  }
};

export default function ThanksWrapperArea(props) {
  const { session } = useContext(SessionContext);
  const currentCurrency = session.currency;

  const API_URL = `${process.env.REACT_APP_API_FOLDER}get_booking/${props.ctype}/${props.hash}`;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const fetcher = url =>
    fetch(url, { headers: { "X-API-TOKEN": API_TOKEN } })
      .then(res => {
        if (!res.ok) throw new Error(`Network response was not ok: ${res.status}`);
        return res.json();
      });

  const { data: booking = {}, error, isLoading } = useSWR(API_URL, fetcher);


  // Prevent duplicate Purchase events during React rerenders in this page load.
  const purchaseTrackedRef = useRef("");

  useEffect(() => {
    const paymentReference = String(
      booking.stripe_payment_intent || booking.stripe_subscription || ""
    ).trim();

    // In this booking system, either Stripe reference confirms payment.
    if (!paymentReference) {
      return;
    }

    const purchaseValue = Number.parseFloat(booking.amount);
    const purchaseCurrency = String(booking.currency || "").trim().toUpperCase();

    const rawCourseId = String(booking.course_id || "").trim();
    const fallbackBookingId = String(booking.id || "").trim();
    const contentId =
      rawCourseId || (fallbackBookingId ? `${props.ctype}:${fallbackBookingId}` : "");

    if (
      !Number.isFinite(purchaseValue) ||
      purchaseValue <= 0 ||
      !/^[A-Z]{3}$/.test(purchaseCurrency) ||
      !contentId
    ) {
      console.warn("Meta Pixel Purchase event not sent: invalid purchase data.", {
        has_payment_reference: true,
        amount: booking.amount,
        currency: booking.currency,
        course_id: booking.course_id
      });
      return;
    }

    if (purchaseTrackedRef.current === paymentReference) {
      return;
    }

    // Prevent repeat Purchase events when the same confirmation URL is refreshed
    // or revisited in this browser.
    const storageKey = `camp_meta_purchase_${paymentReference}`;
    try {
      if (window.localStorage.getItem(storageKey) === "1") {
        purchaseTrackedRef.current = paymentReference;
        return;
      }
    } catch (error) {
      // localStorage may be unavailable; the in-memory guard still applies.
      console.warn("Could not read Meta Purchase duplicate guard:", error);
    }

    const eventID = `camp_purchase_${paymentReference}`;

    const sent = trackMetaPurchase(
      {
        content_ids: [contentId],
        content_type: "product",
        content_category: props.ctype || "workshop",
        value: purchaseValue,
        currency: purchaseCurrency,
        num_items: 1
      },
      eventID
    );

    if (sent) {
      purchaseTrackedRef.current = paymentReference;

      try {
        window.localStorage.setItem(storageKey, "1");
      } catch (error) {
        console.warn("Could not save Meta Purchase duplicate guard:", error);
      }
    }
  }, [
    booking.stripe_payment_intent,
    booking.stripe_subscription,
    booking.amount,
    booking.currency,
    booking.course_id,
    booking.id,
    props.ctype
  ]);

  if (isLoading) {
    return (
      <div className="contact-wrapper pt-110">
        <div className="container">
          <div className="row align-items-center justify-content-lg-center gy-5">
            <div className="col-lg-6">
              <Skeleton height={300} />
            </div>
            <div className="col-lg-5">
              <Skeleton height={40} width="50%" style={{ marginBottom: 16 }} />
              <Skeleton count={2} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return <p className="text-danger">Error loading booking: {error.message}</p>;
  }

  return (
    <div className="contact-wrapper pt-110">
      <div className="container">
        <div className="row align-items-center justify-content-lg-center gy-5">
          <div className="col-lg-6">
            <div className="contatc-intro-figure">
              <img
                src={`${process.env.PUBLIC_URL}/images/banner/booked-bg.png`}
                alt="ContactBannerIMG"
                className="img-fluid"
              />
            </div>
          </div>
          <div className="col-lg-5">
            <div className="booked-info">
              <h3>All done!</h3>
              <p>
                {props.ctype === "onsite" || props.ctype === "residency" ? (
                  "You’re going to CAMP! "
                ) : props.ctype === "online" ? (
                  "You’re going to CAMP online! "
                ) : props.ctype === "mentorship" ? (
                  "Your mentorship credit(s) are ready to use! "
                ) : (
                  ""
                )}
                We’ve sent an email to{" "}
                <span className="sent_email">{booking.email}</span> with
                confirmation of your booking, and links to the CAMP Hub. If you
                don’t receive the mail, drop us a line at{" "}
                <a href="mailto:hello@campfr.com">hello@campfr.com</a>.
              </p>
              {(props.ctype === "onsite" || props.ctype === "residency") && (
                <p>We look forward to welcoming you to CAMP!</p>
              )}

              {props.ctype === "online" && (
                <p>
                  We look forward to meeting you for this unique online workshop!
                </p>
              )}

              {props.ctype === "mentorship" && (
                <p>
                  <a href="https://hub.campfr.com/">Click here</a> to go back to the Hub and start booking your mentorship sessions.
                </p>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
